import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

export default function AmortizationCalc() {
  const [principal, setPrincipal] = useState(250000);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(30);

  const schedule = useMemo(() => {
    const monthlyRate = rate / 100 / 12;
    const n = years * 12;
    if (monthlyRate === 0) return { payment: principal / n, totalInterest: 0, totalPaid: principal, rows: [] };
    const payment = (principal * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    let balance = principal;
    let totalInterest = 0;
    const rows = [];
    for (let year = 1; year <= years; year++) {
      let yearInterest = 0;
      let yearPrincipal = 0;
      for (let m = 0; m < 12; m++) {
        const interest = balance * monthlyRate;
        const princ = payment - interest;
        yearInterest += interest;
        yearPrincipal += princ;
        balance -= princ;
      }
      totalInterest += yearInterest;
      rows.push({ year, principal: yearPrincipal, interest: yearInterest, balance: Math.max(0, balance) });
    }
    return { payment, totalInterest, totalPaid: principal + totalInterest, rows };
  }, [principal, rate, years]);

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  return (
    <Card className="bg-white rounded-3xl border border-stone-200 shadow-sm" data-testid="amortization-calculator">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-xl font-bold text-stone-900">Amortization Calculator</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Loan Amount</Label>
          <Input type="number" value={principal} onChange={(e) => setPrincipal(Number(e.target.value))} className="text-stone-900" data-testid="amort-principal" />
          <Slider value={[principal]} onValueChange={(v) => setPrincipal(v[0])} min={50000} max={2000000} step={5000} className="mt-2" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Interest Rate (%)</Label>
            <Input type="number" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="text-stone-900" data-testid="amort-rate" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Term (Years)</Label>
            <Input type="number" value={years} onChange={(e) => setYears(Number(e.target.value))} className="text-stone-900" data-testid="amort-years" />
          </div>
        </div>
        <div className="bg-emerald-50 rounded-2xl p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-stone-600 text-sm">Monthly Payment</span>
            <span className="font-outfit font-bold text-emerald-900 text-lg" data-testid="amort-monthly">{fmt(schedule.payment)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-600 text-sm">Total Interest</span>
            <span className="font-semibold text-stone-700" data-testid="amort-total-interest">{fmt(schedule.totalInterest)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-600 text-sm">Total Paid</span>
            <span className="font-semibold text-stone-700" data-testid="amort-total-paid">{fmt(schedule.totalPaid)}</span>
          </div>
        </div>
        {/* Mini schedule */}
        <div className="max-h-40 overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-stone-500 border-b border-stone-100">
                <th className="text-left py-1">Year</th>
                <th className="text-right py-1">Principal</th>
                <th className="text-right py-1">Interest</th>
                <th className="text-right py-1">Balance</th>
              </tr>
            </thead>
            <tbody>
              {schedule.rows.slice(0, 10).map(r => (
                <tr key={r.year} className="border-b border-stone-50">
                  <td className="py-1 text-stone-700">{r.year}</td>
                  <td className="py-1 text-right text-stone-600">{fmt(r.principal)}</td>
                  <td className="py-1 text-right text-stone-600">{fmt(r.interest)}</td>
                  <td className="py-1 text-right text-stone-700">{fmt(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
